import { COLORS } from 'assets/styled';
import { respondTo } from 'assets/styled/breakpoints';
import styled, { css } from 'styled-components';

type StyledProps = {
  $color: string;
  isSelected: boolean;
  $editable?: boolean;
  $disabled?: boolean;
};

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 50rem 15rem;
  max-width: 1440rem;
  width: 100%;
  align-self: center;

  ${respondTo.midTablet`
    padding: 30rem 15rem;
  `}

  ${respondTo.smallTablet`
    padding: 20rem 15rem;
  `}
`;

export const BackWrapper = styled.div`
  margin-bottom: 25rem;
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30rem;
  gap: 20rem;
`;

export const Title = styled.span`
  display: block;
  font-weight: 400;
  font-size: 40rem;
  color: ${COLORS.BLACK};

  ${respondTo.smallTablet`
    font-size: 30rem;
  `}

  ${respondTo.bigPhone`
    font-size: 20rem;
  `}
`;

export const SubTitle = styled.span`
  display: block;
  font-weight: 400;
  font-size: 20rem;
  color: ${COLORS.GREEN};
`;

export const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

export const StatusLabel = styled.label<StyledProps>`
  font-size: 30rem;
  font-weight: 300;
  cursor: ${(props: StyledProps) => (props.$editable ? 'pointer' : 'default')};

  ${respondTo.smallTablet`
    font-size: 20rem;
  `}

  ${respondTo.bigPhone`
    font-size: 15rem;
  `}

  color: ${(props: StyledProps) => props.$color};
`;

export const IconWrapper = styled.div`
  display: flex;
  width: 15rem;
  height: 15rem;
  margin-bottom: 15rem;
  margin-left: 10rem;
  cursor: pointer;
  align-items: center;
  justify-content: center;
`;

export const StatusOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10rem;
  background-color: ${COLORS.WHITE};
  border-radius: 10px;
  padding: 10rem;
  position: absolute;
  width: 200rem;
  border: 1px solid ${COLORS.GRAY_200};
  z-index: 3;
  top: 40rem;

  ${respondTo.bigPhone`
    right: -20rem;
  `}
`;

export const StatusOptionLabel = styled.div`
  color: ${COLORS.BLACK};
  ${(props: StyledProps) =>
    props.isSelected &&
    css`
      color: ${COLORS.GREEN};
    `}

  font-size: 14rem;
  font-weight: 300;
  line-height: 21rem;
  letter-spacing: 0em;
  text-align: left;
  padding-bottom: 10rem;
  padding-top: 10rem;
  padding-left: 10px;
  cursor: pointer;
  border-bottom: 1px solid ${COLORS.GRAY_200};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${COLORS.GRAY_300};
  }
`;

export const RetryWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 30rem;
  font-weight: 300;
  cursor: ${({ $disabled }: { $disabled?: boolean }) =>
    $disabled ? 'default' : 'pointer'};
  opacity: ${({ $disabled }: { $disabled?: boolean }) => ($disabled ? 0.5 : 1)};
  color: ${COLORS.BLACK};

  ${respondTo.smallTablet`
    font-size: 20rem;
  `}

  ${respondTo.bigPhone`
    font-size: 15rem;
  `}
`;

export const CardsWrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 50rem;
  margin-bottom: 30rem;

  ${respondTo.midTablet`
    flex-direction: column;
    gap: 20rem;
    margin-bottom: 20rem;
  `}
`;

export const ProductsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  background-color: ${COLORS.WHITE};
  padding: 30rem;
  border-radius: 10rem;
`;

export const ProductsTitle = styled.span`
  font-size: 30rem;
  font-weight: 400;
  color: ${COLORS.BLACK};
  margin-bottom: 25rem;
`;

export const PricingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5rem;
  align-items: flex-end;
  margin-left: auto;
`;

export const PricingRow = styled.div`
  display: flex;
  gap: 20rem;

  ${respondTo.bigPhone`
    width: 100%;
    justify-content: space-between;
  `}
`;

export const PricingLabel = styled.span`
  font-size: 22rem;
  font-weight: 400;
  color: ${COLORS.BLACK};
  display: block;

  ${respondTo.smallTablet`
    font-size: 20rem;
  `}
`;

export const PricingValue = styled.span`
  font-size: 22rem;
  font-weight: 400;
  color: ${COLORS.BLACK};
  display: block;
  width: 200rem;
  text-align: right;

  ${respondTo.smallTablet`
    font-size: 20rem;
  `}

  ${respondTo.bigPhone`
    width: auto;
  `}
`;

export const PricingRowTotal = styled(PricingRow)`
  border-top: 1px solid ${COLORS.BLACK};
  padding-top: 10rem;
  margin-top: 10rem;
`;

export const PricingValueTotal = styled(PricingValue)`
  color: ${COLORS.GREEN};
  font-weight: bold;
`;

export const BottomWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  ${respondTo.midTablet`
    flex-direction: column;
    gap: 20rem;
    align-items: flex-end;
  `}
`;

export const AdditionalCartDetailsWrapper = styled.div`
  max-width: 50%;
  width: 100%;

  ${respondTo.midTablet`
    max-width: 100%;
  `}
`;

export const AdditionalCartDetailsTitle = styled.span`
  font-size: 20rem;
  font-weight: bold;
  color: ${COLORS.BLACK};
  display: block;
`;

export const AdditionalCartDetailsContent = styled.span`
  font-size: 18rem;
  font-weight: 300;
  color: ${COLORS.BLACK};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  word-break: break-word;
  margin-top: 10rem;
`;
